import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ProjectsPage.scss';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import Loader from '../../shared/components/common/loader/Loader';
import Title from '../../shared/components/title/Title';
import { confirmDialog, ConfirmDialog, ConfirmDialogReturn } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { PROJECTS, PROJECTS_ADD } from '../../shared/routes/path';
import { NavLink, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { duplicateProject, removeProjectsBulk } from '../../shared/services/projects/ProjectService';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { FilterMatchMode } from 'primereact/api';
import { ProjectResponse } from '../../shared/models/projects/ProjectResponse';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import LimitTooltipComponent from '../../shared/components/common/limit-tooltip/LimitTooltipComponent';
import ProjectConfiguration from '../../shared/components/common/project-configuration/ProjectConfiguration';
import { getAllUsers } from '../../shared/services/admin/users/AdminUsersService';
import EmptyState from '../../shared/components/common/empty-state/EmptyState';
import useCanAddProject from '../../shared/hooks/account/UseAddProject';
import MoreBtn from '../../shared/components/common/more-btn/MoreBtn';
import useRemoveProject from '../../shared/hooks/projects/UseRemoveProject';
import useChangeName from '../../shared/hooks/projects/UseChangeName';
import { getProjectsFromAccount } from '../../shared/helpers/project/ProjectHelper';
import { canShowModel } from '../../shared/utils/utils';
import ModelTooltips from '../../shared/components/model-tooltips/ModelTooltips';
import ActionBtn from './components/ActionBtn';
import CustomTooltip from '../../shared/components/common/tooltip/CustomTooltip';
import { useProjectGroups, useAssignProjectToGroup, useAllProjectGroups, useBulkAssignProjectsToGroup, useReorderProjectGroups } from '../../shared/hooks/projects/UseProjectGroups';
import ProjectGroupHeader from './components/project-group-header/ProjectGroupHeader';
import { ProjectGroup } from '../../shared/models/projects/ProjectGroup';
import AccountGroupHeader from './components/account-group-header/AccountGroupHeader';
import CreateGroupDialog from './components/create-group-dialog/CreateGroupDialog';
import MoveToGroupMenu from './components/move-to-group-menu/MoveToGroupMenu';
import { Menu } from 'primereact/menu';
import { Tooltip } from 'primereact/tooltip';

const DebouncedSearch: React.FC<{ onSearch: (value: string) => void; delay?: number }> = ({ onSearch, delay = 300 }) => {
  const [value, setValue] = useState('');
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    const timer = setTimeout(() => onSearchRef.current(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return (
    <div className="input-search-box">
      <InputText
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search..."
        className="input-search"
      />
    </div>
  );
};

const ProjectsPage: React.FC = () => {
  const { accountResponse, projectResponse, refetchData, refetchProjectsSilently } = useAccount();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loader, setLoader] = useState<boolean>(false);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<ProjectResponse[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());
  const [collapsedAccounts, setCollapsedAccounts] = useState<Set<number>>(new Set());
  const [createGroupVisible, setCreateGroupVisible] = useState(false);
  const [ungroupedCollapsed, setUngroupedCollapsed] = useState(false);
  const [collapsedUngrouped, setCollapsedUngrouped] = useState<Set<number>>(new Set());

  const { data: groups = [] } = useProjectGroups();
  const { mutate: assignToGroup } = useAssignProjectToGroup();
  const { mutate: bulkAssignToGroup } = useBulkAssignProjectsToGroup();
  const { mutate: reorderProjectGroups } = useReorderProjectGroups();

  const [draggedGroupId, setDraggedGroupId] = useState<number | null>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<number | null>(null);
  const [dragOverGroupIdForProject, setDragOverGroupIdForProject] = useState<number | 'ungrouped' | null>(null);
  const [draggedProjectId, setDraggedProjectId] = useState<number | null>(null);
  const groupedContentRef = useRef<HTMLDivElement>(null);
  const moveMenuRefs = useRef<Map<number, Menu>>(new Map());
  const [openMoveMenuId, setOpenMoveMenuId] = useState<number | null>(null);

  // Determine account-level grouping permission before hooks that use it as enabled flag
  const canViewClientProjectGroups = !!accountResponse?.superadminPermissions?.canViewClientProjectGroups;
  const { data: allGroups = [] } = useAllProjectGroups(canViewClientProjectGroups);

  const { canAddProject, isBillingLimit } = useCanAddProject();

  useEffect(() => {
    if (!groupedContentRef.current) return;
    const rows = groupedContentRef.current.querySelectorAll('tbody tr');
    rows.forEach((tr) => {
      tr.setAttribute('draggable', 'true');
      tr.querySelectorAll('td').forEach((td) => td.setAttribute('draggable', 'true'));
    });
  });

  useEffect(() => {
    if (!openMoveMenuId) return;
    const handleScroll = () => {
      const ref = moveMenuRefs.current.get(openMoveMenuId);
      ref?.hide({} as React.SyntheticEvent);
      setOpenMoveMenuId(null);
    };
    const layoutElement = document.querySelector('.layout__right');
    if (layoutElement) {
      layoutElement.addEventListener('scroll', handleScroll);
      return () => layoutElement.removeEventListener('scroll', handleScroll);
    }
  }, [openMoveMenuId]);

  const { editName, resetValues } = useChangeName({
    onRefresh: () => {
      refreshProjects();
    },
  });

  const { confirmRemove } = useRemoveProject({
    refreshProjects: () => {
      resetValues();
      setLoader(false);
      refetchData();
    },
    setLoader,
  });

  const confirmDuplicate = (id: number) => {
    confirmDialog({
      message: t('areYouSureProceed'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => duplicate(id),
    });
  };

  const dt = useRef<DataTable<ProjectResponse[]>>(null);
  const confirmDialogRef = useRef<ConfirmDialogReturn | null>(null);

  const openConfiguration = (projectId: number, demoProject: string | null, ownerAccountId: number) => {
    if (!projectResponse || (!accountResponse?.isSuperAdmin && !accountResponse?.isLimitedSuperAdmin)) {
      return;
    }

    getAllUsers()
      .then((allAccounts) => {
        confirmDialogRef.current = confirmDialog({
          message: (
            <ProjectConfiguration
              allAccounts={allAccounts}
              demoProject={demoProject}
              ownerAccountId={ownerAccountId}
              projectId={projectId}
              onSave={() => hideDialog()}
            />
          ),
          header: t('configuration'),
          footer: () => null,
        });
      })
      .catch(() => {});
  };

  const hideDialog = () => {
    if (!confirmDialogRef.current) {
      return;
    }

    confirmDialogRef.current?.hide();
    refreshProjects();
  };

  const duplicate = (id: number) => {
    if (!canAddProject) {
      return;
    }

    setLoader(true);
    duplicateProject(id)
      .then(() => {
        setLoader(false);
        refetchData();
      })
      .catch(() => {
        setLoader(false);
      });
  };

  const refreshProjects = () => {
    resetValues();
    setLoader(false);
    refetchProjectsSilently();
  };

  const confirmBulkRemove = () => {
    confirmDialog({
      message: t('areYouSureProceed'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => bulkRemove(),
    });
  };

  const bulkRemove = async () => {
    setLoader(true);
    const ids = selectedProjects.map((p) => p.id);
    try {
      await removeProjectsBulk(ids);
      setSelectedProjects([]);
      refreshProjects();
    } catch {
      setLoader(false);
    }
  };

  const toggleGroupCollapse = (groupId: number) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleAccountCollapse = (accountId: number) => {
    setCollapsedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const toggleUngroupedCollapse = (accountId?: number) => {
    if (accountId !== undefined) {
      setCollapsedUngrouped((prev) => {
        const next = new Set(prev);
        if (next.has(accountId)) next.delete(accountId);
        else next.add(accountId);
        return next;
      });
    } else {
      setUngroupedCollapsed((prev) => !prev);
    }
  };

  const filteredProjects = useMemo(
    () => projectResponse && accountResponse
      ? getProjectsFromAccount(accountResponse, projectResponse)
      : [],
    [projectResponse, accountResponse]
  );

  // Apply global text filter
  const searchFilteredProjects = useMemo(() => {
    if (!globalFilterValue) return filteredProjects;
    const query = globalFilterValue.toLowerCase();
    return filteredProjects.filter((p) =>
      p.projectLabel.toLowerCase().includes(query) ||
      p.defaultKeyword?.toLowerCase().includes(query) ||
      p.domain?.toLowerCase().includes(query)
    );
  }, [filteredProjects, globalFilterValue]);

  const projectsByGroup = useMemo(() => {
    const validGroupIds = new Set(groups.map((g) => g.id));
    const map = new Map<number | null, ProjectResponse[]>();
    for (const project of searchFilteredProjects) {
      const groupId = project.projectGroupId;
      const key = groupId != null && validGroupIds.has(groupId) ? groupId : null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(project);
    }
    return map;
  }, [searchFilteredProjects, groups]);

  // Account-level grouping for superadmins with client project group visibility
  const accountsData = useMemo(() => {
    if (!canViewClientProjectGroups) return null;

    const accountMap = new Map<number, { email: string; projects: ProjectResponse[] }>();
    for (const project of searchFilteredProjects) {
      if (!accountMap.has(project.accountId)) {
        accountMap.set(project.accountId, {
          email: project.additionalInfo?.ownerEmail || `Account #${project.accountId}`,
          projects: [],
        });
      }
      accountMap.get(project.accountId)!.projects.push(project);
    }

    // Sort: superadmin's own account last, rest alphabetically by email
    const ownAccountId = accountResponse?.accountId;
    const entries = Array.from(accountMap.entries());
    entries.sort((a, b) => {
      if (a[0] === ownAccountId) return 1;
      if (b[0] === ownAccountId) return -1;
      return a[1].email.localeCompare(b[1].email);
    });

    return entries;
  }, [canViewClientProjectGroups, searchFilteredProjects, accountResponse?.accountId]);

  if (!projectResponse || !accountResponse || loader || canAddProject === null) {
    return (
      <div className="page-content">
        <div className="page-box">
          <Loader />
        </div>
      </div>
    );
  }

  const hasProjectGroupsAddon = accountResponse.addons?.includes('PROJECT_GROUPS');

  // Group projects (for non-superadmin grouped mode)
  const hasGroups = groups.length > 0 && hasProjectGroupsAddon;
  const sortedGroups = [...groups].sort((a, b) => a.position - b.position);
  const ungroupedProjects = projectsByGroup.get(null) || [];

  // Helpers for per-account group rendering in superadmin mode
  const getGroupsForAccount = (accountId: number) =>
    allGroups.filter((g) => g.accountId === accountId).sort((a, b) => a.position - b.position);

  const getProjectsByGroupMap = (projects: ProjectResponse[], accountGroups: ProjectGroup[]) => {
    const validGroupIds = new Set(accountGroups.map((g) => g.id));
    const map = new Map<number | null, ProjectResponse[]>();
    for (const project of projects) {
      const groupId = project.projectGroupId;
      const key = groupId != null && validGroupIds.has(groupId) ? groupId : null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(project);
    }
    return map;
  };

  const handleGroupDragStart = (e: React.DragEvent, groupId: number) => {
    setDraggedGroupId(groupId);
    e.dataTransfer.setData('application/group-id', String(groupId));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleGroupDragOver = (e: React.DragEvent, groupId: number) => {
    if (!e.dataTransfer.types.includes('application/group-id')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverGroupId !== groupId) {
      setDragOverGroupId(groupId);
    }
  };

  const handleGroupDrop = (e: React.DragEvent, targetGroupId: number) => {
    if (!e.dataTransfer.types.includes('application/group-id')) return;
    e.preventDefault();
    setDragOverGroupId(null);
    if (!draggedGroupId || draggedGroupId === targetGroupId) {
      setDraggedGroupId(null);
      return;
    }
    const ids = sortedGroups.map((g) => g.id);
    const fromIndex = ids.indexOf(draggedGroupId);
    const toIndex = ids.indexOf(targetGroupId);
    if (fromIndex === -1 || toIndex === -1) {
      setDraggedGroupId(null);
      return;
    }
    const newIds = [...ids];
    newIds.splice(fromIndex, 1);
    newIds.splice(toIndex, 0, draggedGroupId);
    reorderProjectGroups(newIds);
    setDraggedGroupId(null);
  };

  const handleGroupDragEnd = () => {
    setDraggedGroupId(null);
    setDragOverGroupId(null);
  };

  const handleRowDragStart = (e: React.DragEvent) => {
    const tr = (e.target as HTMLElement).closest('tr');
    if (!tr) return;
    const marker = tr.querySelector<HTMLElement>('[data-project-id]');
    if (!marker) return;
    const projectId = marker.dataset.projectId;
    if (!projectId) return;
    e.dataTransfer.setData('application/project-id', projectId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedProjectId(Number(projectId));

    const ghost = document.createElement('div');
    ghost.className = 'project-drag-ghost';
    const project = filteredProjects.find((p) => p.id === Number(projectId));
    ghost.textContent = project?.projectLabel || `Project #${projectId}`;
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    requestAnimationFrame(() => document.body.removeChild(ghost));
  };

  const handleProjectDragOver = (e: React.DragEvent, groupId: number | 'ungrouped') => {
    if (!e.dataTransfer.types.includes('application/project-id')) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverGroupIdForProject !== groupId) {
      setDragOverGroupIdForProject(groupId);
    }
  };

  const handleProjectDrop = (e: React.DragEvent, targetGroupId: number | 'ungrouped') => {
    const projectIdStr = e.dataTransfer.getData('application/project-id');
    if (!projectIdStr) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverGroupIdForProject(null);
    const projectId = Number(projectIdStr);
    const newGroupId = targetGroupId === 'ungrouped' ? null : targetGroupId;
    assignToGroup({ projectId, groupId: newGroupId });
  };

  const handleProjectDragEnd = () => {
    setDragOverGroupIdForProject(null);
    setDraggedProjectId(null);
  };

  const buttonAndSearch = () => {
    return (
      <div className="page-box__buttons">
        {selectionMode && selectedProjects.length > 0 && (
          <div className="page-box__buttons-panel">
            {(hasProjectGroupsAddon || canViewClientProjectGroups) && groups.length > 0 && selectedProjects.some((p) => !p.demoVersion) && (
              <MoveToGroupMenu
                groups={groups}
                currentGroupId={null}
                onSelect={(groupId) => {
                  bulkAssignToGroup({
                    projectIds: selectedProjects.filter((p) => !p.demoVersion).map((p) => p.id),
                    groupId,
                  });
                  setSelectedProjects([]);
                }}
                buttonId="bulk-move-to-group-btn"
              />
            )}
            <Button
              className="action-btn action-btn--danger"
              onClick={confirmBulkRemove}
              id="bulk-remove-projects-btn"
            >
              <span className="ico--trash ico--xs" />
            </Button>
            <CustomTooltip tooltipText="bulkRemove" targetRef="bulk-remove-projects-btn" />
          </div>
        )}
        <DebouncedSearch onSearch={setGlobalFilterValue} />
        {getAddProjectButton()}
        <Button
          className="action-btn"
          onClick={() => dt.current?.exportCSV()}
          id="export-csv-btn"
        >
          <span className="ico--download ico--xs" />
        </Button>
        <CustomTooltip tooltipText="exportCsv" targetRef="export-csv-btn" />

        {(hasProjectGroupsAddon || canViewClientProjectGroups) && (
          <Button
            className={`btn btn--sm btn--secondary`}
            onClick={() => setCreateGroupVisible(true)}
            label={t('createGroup')}
            id="create-group-btn"
            icon={<span className="ico--add ico--xs" />}
          />
        )}
        <Button
          className={`action-btn${selectionMode ? ' action-btn--active' : ''}`}
          onClick={() => {
            setSelectionMode((prev) => {
              if (prev) setSelectedProjects([]);
              return !prev;
            });
          }}
          id="select-mode-projects-btn"
        >
          <span className="ico--select ico--xs" />
        </Button>
        <CustomTooltip tooltipText="selectModeProjects" targetRef="select-mode-projects-btn" />
      </div>
    );
  };

  const getAddProjectButton = () => {
    if (['BLOCKED'].includes(accountResponse.accountStatus)) {
      return <></>;
    }

    const tooltipText = isBillingLimit ? 'reachedLimitProject' : undefined;

    return (
      <>
        <Button
          className={`btn btn--sm ${!canAddProject ? 'btn--disabled-tooltip-color' : ''}`}
          onClick={() => {
            if (canAddProject) {
              navigate(PROJECTS_ADD);
            }
          }}
          label={t('addNewProject')}
          id="add-prompt"
          icon={<span className="ico--add ico--xs" />}
        />
        {!canAddProject && (
          <LimitTooltipComponent tooltipText={tooltipText} id="add-prompt" direction="bottom" />
        )}
      </>
    );
  };

  const emptyStateData = {
    title: t('emptyStateProject'),
    imageClassName: 'img--project',
    children: getAddProjectButton(),
  };


  const emptyTemplate = () => {
    return (
      <div className="no-results">
        <div className="no-results__icon material-symbols-outlined">search</div>
        <div className="no-results__title">{t('noResultsFound')}</div>
        <div className="no-results__desc">{t('noResultsFoundDesc')}</div>
      </div>
    );
  };

  const emptyGroupTemplate = () => {
    return (
      <div className="no-results no-results--compact">
        <div className="no-results__title">{globalFilterValue ? t('noResultsFound') : t('emptyProjectGroup')}</div>
      </div>
    );
  };

  const projectNameBodyTemplate = (project: ProjectResponse) => {
    const period = project.defaultPeriod.toLowerCase();
    return (
      <div className="project-name" data-project-id={project.id}>
        <span className="ico--folder ico--sm" />
        <NavLink to={`${PROJECTS}/${project.id}/${period}/all`}>
          {project.projectLabel} {project.demoVersion && '(Demo)'}
        </NavLink>
        {accountResponse.isSuperAdmin && !accountResponse.isLimitedSuperAdmin && (
          <div className="project-name__additional">
            isSuperAdminProject:
            {project.accountId === 1 ? (
              <span className="project-name__ok">true</span>
            ) : (
              <span>false</span>
            )}
            | accountId: {project.accountId} | email:{' '}
            {project.additionalInfo?.ownerEmail ? (
              <a href={`mailto:${project.additionalInfo?.ownerEmail}`} className="link">
                {project.additionalInfo?.ownerEmail}
              </a>
            ) : (
              <span>-</span>
            )}{' '}
            | domain:{' '}
            {project.domain ? (
              <a href={`http://${project.domain}`} className="link" target="_blank">
                {project.domain}
              </a>
            ) : (
              <span>-</span>
            )}{' '}
            | brandName: {project.firmName} |
            <Button
              className="btn--link btn--link-blue"
              label="configuration"
              onClick={() => openConfiguration(project.id, project.demoVersion, project.accountId)}
            />
          </div>
        )}
      </div>
    );
  };

  const statsBodyTemplate = (project: ProjectResponse) => {
    const period = project.defaultPeriod.toLowerCase();
    return (
      <div className="tags tags--nowrap">
        {canShowModel('GPT', accountResponse.features) && (
          <span
            className="ico--gpt-bck ico--md"
            onClick={() => navigate(`${PROJECTS}/${project.id}/${period}/gpt`)}
          />
        )}

        {canShowModel('GEMINI', accountResponse.features) && (
          <span
            className="ico--gemini-bck ico--md"
            onClick={() => navigate(`${PROJECTS}/${project.id}/${period}/gemini`)}
          />
        )}

        {canShowModel('CLAUDE', accountResponse.features) && (
          <span
            className="ico--claude-bck ico--md"
            onClick={() => navigate(`${PROJECTS}/${project.id}/${period}/claude`)}
          />
        )}

        {canShowModel('COPILOT', accountResponse.features) && (
          <span
            className="ico--copilot-bck ico--md"
            onClick={() => navigate(`${PROJECTS}/${project.id}/${period}/copilot_browser`)}
          />
        )}

        {canShowModel('GROK', accountResponse.features) && (
          <span
            className="ico--grok-bck ico--md"
            onClick={() => navigate(`${PROJECTS}/${project.id}/${period}/grok_browser`)}
          />
        )}

        {canShowModel('AI_OVERVIEW_NEW', accountResponse.features) && (
          <span
            className="ico--ai-overview-bck ico--md"
            onClick={() => navigate(`${PROJECTS}/${project.id}/${period}/ai_overview_new_browser`)}
          />
        )}

        {canShowModel('AI_MODE', accountResponse.features) && (
          <span
            className="ico--ai-mode ico--md"
            onClick={() => navigate(`${PROJECTS}/${project.id}/${period}/ai_mode_browser`)}
          />
        )}

        {canShowModel('DEEP_SEEK', accountResponse.features) && (
          <span
            className="ico--deepseek-bck ico--md"
            onClick={() => navigate(`${PROJECTS}/${project.id}/${period}/deep_seek`)}
          />
        )}

        {canShowModel('PERPLEXITY', accountResponse.features) && (
          <span
            className="ico--perplexity-bck ico--md"
            onClick={() => navigate(`${PROJECTS}/${project.id}/${period}/perplexity`)}
          />
        )}
      </div>
    );
  };

  const renderActionColumn = (project: ProjectResponse) => {
    const isOwnProject = project.accountId === accountResponse.accountId;
    const hasGroupAccess = hasProjectGroupsAddon || canViewClientProjectGroups;
    const canMoveToGroup = isOwnProject && !project.demoVersion && groups.length > 0 && hasGroupAccess;

    const availableGroupsForRow = canMoveToGroup
      ? groups.filter((g) => g.id !== project.projectGroupId)
      : [];

    const handleAssign = (groupId: number | null) => {
      const ref = moveMenuRefs.current.get(project.id);
      ref?.hide({} as React.SyntheticEvent);
      assignToGroup({ projectId: project.id, groupId });
    };

    const moveMenuModel = [
      ...(project.projectGroupId !== null && project.projectGroupId !== undefined
        ? [
            {
              template: () => (
                <div
                  className="move-to-group-menu__item"
                  onClick={() => handleAssign(null)}
                >
                  <span>{t('ungrouped')}</span>
                </div>
              ),
            },
            ...(availableGroupsForRow.length > 0 ? [{ separator: true }] : []),
          ]
        : []),
      ...availableGroupsForRow.map((group) => ({
        template: () => (
          <div
            className="move-to-group-menu__item"
            onClick={() => handleAssign(group.id)}
          >
            <span
              className="move-to-group-menu__dot"
              style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
            />
            <span id={`move-group-project-${project.id}-${group.id}`} className="move-to-group-menu__name">{group.name}</span>
            <Tooltip target={`#move-group-project-${project.id}-${group.id}`} content={group.name} position="top" showDelay={300} />
          </div>
        ),
      })),
    ];

    return (
      <div className="table__cell--actions">
        <MoreBtn
          children={
            <ActionBtn
              confirmDuplicate={confirmDuplicate}
              projectId={project.id}
              confirmRemove={confirmRemove}
              accountStatus={accountResponse?.accountStatus}
              demoVersion={project.demoVersion}
              editName={editName}
              isSuperAdminPermission={
                accountResponse?.isSuperAdmin && !accountResponse.isLimitedSuperAdmin
              }
              canMoveToGroup={canMoveToGroup}
              onMoveClick={(e) => {
                const ref = moveMenuRefs.current.get(project.id);
                ref?.toggle(e);
                setOpenMoveMenuId(project.id);
              }}
            />
          }
          rowId={project.id || 0}
        />
        {canMoveToGroup && (
          <Menu
            ref={(el) => {
              if (el) moveMenuRefs.current.set(project.id, el);
              else moveMenuRefs.current.delete(project.id);
            }}
            popup
            appendTo={document.body}
            className="move-to-group-menu__popup"
            onHide={() => setOpenMoveMenuId(null)}
            model={moveMenuModel}
          />
        )}
      </div>
    );
  };

  const getColumns = (projects: ProjectResponse[]) => {
    const cols = [
      <Column
        key="projectLabel"
        sortable
        field="projectLabel"
        header={t('projectName')}
        body={projectNameBodyTemplate}
        headerClassName="table__header"
        style={{ minWidth: '200px', maxWidth: '100%' }}
      />,
      <Column key="defaultKeyword" field="defaultKeyword" header={t('defaultKeyword')} headerClassName="table__header" hidden={true} />,
      <Column key="industry" field="industry" header={t('industry')} hidden={true} />,
      <Column key="domain" field="domain" header="domain" headerClassName="table__header" hidden={true} />,
      <Column
        key="stats"
        exportable={false}
        header={t('stats')}
        body={statsBodyTemplate}
        headerClassName="table__header"
        style={{ width: '350px' }}
      />,
      <Column
        key="created"
        field="created"
        header={t('created')}
        body={(data) => format(data.created, 'yyyy-MM-dd / HH:mm')}
        headerClassName="table__header"
        bodyClassName="table__cell--nowrap"
        style={{ width: '145px' }}
      />,
      <Column
        key="action"
        exportable={false}
        frozen={true}
        alignFrozen="right"
        header={t('action')}
        body={renderActionColumn}
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell--gap"
        align={'center'}
        style={{ width: '34px' }}
      />,
    ];

    if (selectionMode) {
      cols.unshift(
        <Column
          key="checkbox"
          exportable={false}
          headerStyle={{ width: '25px' }}
          header={
            <Checkbox
              checked={
                projects.length > 0 &&
                projects.every((p) => selectedProjects.some((sp) => sp.id === p.id))
              }
              onChange={(e) => {
                if (e.checked) {
                  setSelectedProjects((prev) => {
                    const ids = new Set(prev.map((p) => p.id));
                    return [...prev, ...projects.filter((p) => !ids.has(p.id))];
                  });
                } else {
                  const groupIds = new Set(projects.map((p) => p.id));
                  setSelectedProjects((prev) => prev.filter((p) => !groupIds.has(p.id)));
                }
              }}
            />
          }
          body={(project: ProjectResponse) => (
            <Checkbox
              checked={selectedProjects.some((p) => p.id === project.id)}
              onChange={(e) => {
                if (e.checked) {
                  setSelectedProjects((prev) => [...prev, project]);
                } else {
                  setSelectedProjects((prev) => prev.filter((p) => p.id !== project.id));
                }
              }}
            />
          )}
        />
      );
    }

    return cols;
  };

  const canDragProjects = hasGroups;

  const renderProjectTable = (projects: ProjectResponse[], key: string, isGroupChild = false) => (
    <DataTable
      stripedRows
      paginator
      scrollable
      paginatorClassName="pagination"
      removableSort
      rows={10}
      rowsPerPageOptions={[10, 25, 50]}
      value={projects}
      tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
      tableClassName={`table ${selectionMode ? 'table--select' : ''}${canDragProjects ? ' table--draggable-rows' : ''}`}
      className="table-wrapper"
      emptyMessage={isGroupChild ? emptyGroupTemplate : emptyTemplate}
      currentPageReportTemplate={t('rowsNumber') + ':'}
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      dataKey="id"
      key={key}
      rowClassName={(rowData: ProjectResponse) =>
        draggedProjectId && rowData.id === draggedProjectId ? 'project-row--dragging' : ''
      }
    >
      {getColumns(projects)}
    </DataTable>
  );

  return (
    <>
      <div className="project-page page-content">
        <ConfirmDialog className="dialog-box" />
        <CreateGroupDialog
          visible={createGroupVisible}
          onHide={() => setCreateGroupVisible(false)}
        />

        {filteredProjects.length > 0 ? (
          <div className="page-box" key="page-box-project">
            <div className="page-box__filters" data-observe-sticky>
              <Title title={t('allProjects')} children={buttonAndSearch()} btnLabel={t('addNewProject')} />
            </div>

            {/* Hidden DataTable for CSV export */}
            <DataTable
              ref={dt}
              exportFilename="projects-export"
              value={filteredProjects}
              dataKey="id"
              style={{ display: 'none' }}
            >
              <Column field="projectLabel" header={t('projectName')} />
              <Column field="defaultKeyword" header={t('defaultKeyword')} />
              <Column field="domain" header="domain" />
              <Column
                field="created"
                header={t('created')}
                body={(data) => format(data.created, 'yyyy-MM-dd / HH:mm')}
              />
            </DataTable>

            <div className="table-content">
              {canViewClientProjectGroups && accountsData ? (
                // Full superadmin: account-level grouping
                <>
                  {accountsData.map(([accountId, accountData]) => {
                    const accountGroups = getGroupsForAccount(accountId);
                    const accountProjectsByGroup = getProjectsByGroupMap(accountData.projects, accountGroups);
                    const isAccountCollapsed = collapsedAccounts.has(accountId);

                    return (
                      <div className="account-group" key={`account-${accountId}`}>
                        <AccountGroupHeader
                          email={accountData.email}
                          accountId={accountId}
                          projectCount={accountData.projects.length}
                          isCollapsed={isAccountCollapsed}
                          onToggleCollapse={() => toggleAccountCollapse(accountId)}
                        />
                        {!isAccountCollapsed && (
                          <div className="account-group__content">
                            {accountGroups.length > 0 ? (
                              <>
                                {accountGroups.map((group) => {
                                  const groupProjects = accountProjectsByGroup.get(group.id) || [];
                                  if (groupProjects.length === 0 && globalFilterValue) return null;
                                  const isCollapsed = collapsedGroups.has(group.id);

                                  return (
                                    <div className="project-group" key={`group-${group.id}`}>
                                      <ProjectGroupHeader
                                        group={group}
                                        projectCount={groupProjects.length}
                                        isCollapsed={isCollapsed}
                                        onToggleCollapse={() => toggleGroupCollapse(group.id)}
                                        onDeleteSuccess={refetchProjectsSilently}
                                      />
                                      {!isCollapsed && renderProjectTable(groupProjects, `table-group-${group.id}`)}
                                    </div>
                                  );
                                })}
                                {(accountProjectsByGroup.get(null) || []).length > 0 && (
                                  <div className="project-group">
                                    <div
                                      className={`project-group-header project-group-header--ungrouped${collapsedUngrouped.has(accountId) ? ' project-group-header--collapsed' : ''}`}
                                      onClick={() => toggleUngroupedCollapse(accountId)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <div className="project-group-header__left">
                                        <span className="ico--up ico--xs project-group-header__chevron" />
                                        <span
                                          className="project-group-header__dot"
                                          style={{ backgroundColor: 'var(--grey-ux-300)' }}
                                        />
                                        <span className="project-group-header__name">{t('ungrouped')}</span>
                                        <span className="project-group-header__count">
                                          {(accountProjectsByGroup.get(null) || []).length}
                                        </span>
                                      </div>
                                    </div>
                                    {!collapsedUngrouped.has(accountId) && renderProjectTable(accountProjectsByGroup.get(null) || [], `table-ungrouped-${accountId}`)}
                                  </div>
                                )}
                              </>
                            ) : (
                              renderProjectTable(accountData.projects, `table-account-${accountId}`)
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              ) : hasGroups ? (
                // Regular grouped mode
                <div
                  ref={groupedContentRef}
                  onDragStart={handleRowDragStart}
                  onDragEnd={handleProjectDragEnd}
                >
                  {sortedGroups.map((group) => {
                    const groupProjects = projectsByGroup.get(group.id) || [];
                    if (groupProjects.length === 0 && globalFilterValue) return null;
                    const isCollapsed = collapsedGroups.has(group.id);
                    const isDragging = draggedGroupId === group.id;
                    const isDragOver = dragOverGroupId === group.id && draggedGroupId !== group.id;
                    const isProjectDragOver = dragOverGroupIdForProject === group.id;

                    return (
                      <div
                        className={`project-group${isDragging ? ' project-group--dragging' : ''}${isDragOver || isProjectDragOver ? ' project-group--drag-over' : ''}`}
                        key={`group-${group.id}`}
                        onDragOver={(e) => {
                          handleGroupDragOver(e, group.id);
                          handleProjectDragOver(e, group.id);
                        }}
                        onDrop={(e) => {
                          handleGroupDrop(e, group.id);
                          handleProjectDrop(e, group.id);
                        }}
                        onDragLeave={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                            setDragOverGroupId(null);
                            setDragOverGroupIdForProject(null);
                          }
                        }}
                      >
                        <div
                          draggable
                          onDragStart={(e) => { e.stopPropagation(); handleGroupDragStart(e, group.id); }}
                          onDragEnd={handleGroupDragEnd}
                          className="project-group__header-drag"
                        >
                          <ProjectGroupHeader
                            group={group}
                            projectCount={groupProjects.length}
                            isCollapsed={isCollapsed}
                            onToggleCollapse={() => toggleGroupCollapse(group.id)}
                            onDeleteSuccess={refetchProjectsSilently}
                          />
                        </div>
                        {!isCollapsed && renderProjectTable(groupProjects, `table-group-${group.id}`, true)}
                      </div>
                    );
                  })}

                  {ungroupedProjects.length > 0 && (
                    <div
                      className={`project-group${dragOverGroupIdForProject === 'ungrouped' ? ' project-group--drag-over' : ''}`}
                      onDragOver={(e) => handleProjectDragOver(e, 'ungrouped')}
                      onDrop={(e) => handleProjectDrop(e, 'ungrouped')}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                          setDragOverGroupIdForProject(null);
                        }
                      }}
                    >
                      <div
                        className={`project-group-header project-group-header--ungrouped${ungroupedCollapsed ? ' project-group-header--collapsed' : ''}`}
                        onClick={() => toggleUngroupedCollapse()}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="project-group-header__left">
                          <span className="ico--up ico--xs project-group-header__chevron" />
                          <span
                            className="project-group-header__dot"
                            style={{ backgroundColor: 'var(--grey-ux-300)' }}
                          />
                          <span className="project-group-header__name">{t('ungrouped')}</span>
                          <span className="project-group-header__count">{ungroupedProjects.length}</span>
                        </div>
                      </div>
                      {!ungroupedCollapsed && renderProjectTable(ungroupedProjects, 'table-ungrouped', true)}
                    </div>
                  )}
                </div>
              ) : (
                // Flat table (no groups)
                <DataTable
                  stripedRows
                  paginator
                  scrollable
                  paginatorClassName="pagination"
                  removableSort
                  rows={50}
                  rowsPerPageOptions={[10, 25, 50]}
                  value={searchFilteredProjects}
                  tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
                  tableClassName={`table ${selectionMode ? 'table--select' : ''}`}
                  className="table-wrapper"
                  emptyMessage={emptyTemplate}
                  currentPageReportTemplate={t('rowsNumber') + ':'}
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  dataKey="id"
                >
                  {getColumns(searchFilteredProjects)}
                </DataTable>
              )}
              <ModelTooltips />
            </div>
          </div>
        ) : (
          <div className="empty-state-page">
            <EmptyState emptyStateData={emptyStateData} />
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectsPage;
